/**
 * DriveApi over fetch (contracts routes listDrive…driveChanges, docs/features/files.md) with the session's access
 * token: refreshed before it expires, and once more after a 401. Creates with a clientId send it as the
 * Idempotency-Key.
 */
import type { DriveItemResponse, DriveSharingResponse, DriveStorageResponse, DriveVersionsResponse, ListDriveResponse } from "@bundle/contracts";
import { buildPath, routes, type RouteName } from "@bundle/contracts/routes";
import { ApiFailure, type ApiErrorKind, type DriveApi, type SessionViewModel } from "@bundle/core";
import { API_BASE } from "../env.ts";

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 429) return "rateLimited";
  if (status >= 400 && status < 500) return "invalid";
  return status === 502 || status === 503 || status === 504 ? "network" : "server";
}

export function createDriveApi(session: SessionViewModel, base: string = API_BASE): DriveApi {
  async function call<T>(name: RouteName, params: Record<string, string>, body?: unknown, query?: Record<string, string | undefined>, idempotencyKey?: string): Promise<T> {
    const route = routes[name];
    let url = base + buildPath(route.path, params);
    if (query !== undefined) {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== "") q.set(k, v);
      const s = q.toString();
      if (s !== "") url += `?${s}`;
    }
    const send = async (token: string | undefined) => {
      const headers: Record<string, string> = { accept: "application/json" };
      if (body !== undefined) headers["content-type"] = "application/json";
      if (token !== undefined) headers.authorization = `Bearer ${token}`;
      if (idempotencyKey !== undefined) headers["idempotency-key"] = idempotencyKey;
      try {
        return await fetch(url, { method: route.method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
      } catch (e) {
        throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
      }
    };
    await session.ensureFreshToken();
    let token = session.state.session?.token;
    let res = await send(token);
    if (res.status === 401 && token !== undefined && (await session.authRejected(token))) {
      token = session.state.session?.token;
      res = await send(token);
    }
    if (res.status === 204 || res.status === 202) return undefined as T;
    const text = await res.text();
    let json: unknown;
    try {
      json = text === "" ? undefined : JSON.parse(text);
    } catch {
      json = undefined;
    }
    if (!res.ok) {
      const err = (json as { error?: { code?: string; message?: string } } | undefined)?.error;
      throw new ApiFailure(kindFor(res.status), res.status, err?.code ?? String(res.status), err?.message ?? res.statusText);
    }
    return json as T;
  }

  const item = async (p: Promise<DriveItemResponse>) => (await p).item;
  const versions = async (p: Promise<DriveVersionsResponse>) => (await p).versions;
  return {
    list: (ws, q) =>
      call<ListDriveResponse>("listDrive", { ws }, undefined, { view: q.view, folderId: q.folderId, q: q.q, type: q.type, owner: q.owner, sort: q.sort, dir: q.dir, cursor: q.cursor }),
    get: (ws, itemId, opts) => item(call("getDriveItem", { ws, itemId }, undefined, { via: opts?.viaLink === true ? "link" : undefined })),
    create: (ws, req) => item(call("createDriveItem", { ws }, req, undefined, req.clientId)),
    update: (ws, itemId, req) => item(call("updateDriveItem", { ws, itemId }, req)),
    copy: (ws, itemId, req) => item(call("copyDriveItem", { ws, itemId }, req)),
    versions: (ws, itemId) => versions(call("driveVersions", { ws, itemId })),
    addVersion: (ws, itemId, req) => item(call("addDriveVersion", { ws, itemId }, req)),
    updateVersion: (ws, itemId, versionId, req) => versions(call("updateDriveVersion", { ws, itemId, versionId }, req)),
    restoreVersion: (ws, itemId, versionId) => item(call("restoreDriveVersion", { ws, itemId, versionId })),
    deleteVersion: (ws, itemId, versionId) => versions(call("deleteDriveVersion", { ws, itemId, versionId })),
    sharing: (ws, itemId) => call<DriveSharingResponse>("driveSharing", { ws, itemId }),
    updateSharing: (ws, itemId, req) => call<DriveSharingResponse>("updateDriveSharing", { ws, itemId }, req),
    trash: (ws, itemIds) => call<void>("trashDriveItems", { ws }, { itemIds }),
    restore: (ws, itemIds) => call<void>("restoreDriveItems", { ws }, { itemIds }),
    deleteForever: (ws, itemIds) => call<void>("deleteDriveItems", { ws }, { itemIds }),
    emptyTrash: (ws) => call<void>("emptyDriveTrash", { ws }),
    storage: (ws) => call<DriveStorageResponse>("driveStorage", { ws }),
  };
}
